"use client";

import {
	type InviteTeamMemberInput,
	InviteTeamMemberSchema,
} from "@gosaas/schemas";
import { zodResolver } from "@hookform/resolvers/zod";
import * as React from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useTeam } from "@/components/providers/team";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";

interface InviteTeamMemberDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onInviteSuccess?: () => void;
}

export function InviteTeamMemberDialog({
	open,
	onOpenChange,
	onInviteSuccess,
}: InviteTeamMemberDialogProps) {
	const [isLoading, setIsLoading] = React.useState(false);
	const { teamID, refresh } = useTeam();

	const form = useForm<InviteTeamMemberInput>({
		resolver: zodResolver(InviteTeamMemberSchema),
		defaultValues: {
			email: "",
			role: "member",
		},
	});

	async function onSubmit(data: InviteTeamMemberInput) {
		try {
			setIsLoading(true);
			const response = await requestWithHandling<void>(() =>
				apiClient.api.teamMembersInviteCreate(teamID as string, {
					email: data.email,
					role: data.role,
				}),
			);
			if (response.code !== "SUCCESS") {
				toast.error("We couldn't send the invitation.", {
					description: response.message,
				});
				return;
			}

			toast.success("Team member invited successfully", {
				description:
					"The team member has been invited to your team. Please let them know to check their email for the invite.",
			});

			form.reset();
			onOpenChange(false);
			await refresh();
			onInviteSuccess?.();
		} catch (error) {
			toast.error("Failed to invite team member", {
				description:
					error instanceof TypedClientApiError
						? error.message
						: "An unknown error occurred.",
			});
		} finally {
			setIsLoading(false);
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[425px]">
				<DialogHeader>
					<DialogTitle>Invite Team Member</DialogTitle>
					<DialogDescription>
						Invite a new team member to your team.
					</DialogDescription>
				</DialogHeader>
				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
						<div className="grid grid-cols-2 gap-4">
							<FormField
								control={form.control}
								name="email"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Email</FormLabel>
										<FormControl>
											<Input
												placeholder="Enter email..."
												{...field}
												disabled={isLoading}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
							<FormField
								control={form.control}
								name="role"
								render={({ field }) => (
									<FormItem className="flex-1">
										<FormLabel>Role</FormLabel>
										<Select
											disabled={isLoading}
											onValueChange={field.onChange}
											value={field.value || ""}
										>
											<FormControl>
												<SelectTrigger className="w-full">
													<SelectValue>
														{field.value
															? field.value.charAt(0).toUpperCase() +
																field.value.slice(1)
															: "Select team type"}
													</SelectValue>
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												<SelectItem value="admin">Admin</SelectItem>
												<SelectItem value="billing">Billing</SelectItem>
												<SelectItem value="member">Member</SelectItem>
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<DialogFooter>
							<Button
								type="submit"
								disabled={isLoading}
								className="w-full sm:w-auto"
							>
								{isLoading ? "Inviting..." : "Invite Member"}
							</Button>
						</DialogFooter>
					</form>
				</Form>
			</DialogContent>
		</Dialog>
	);
}
